//
// video-player.js
//

import * as SFX from '../ScrollFX';
import create_element from '../helpers/create-element';

const vidWraps = Array.from(document.querySelectorAll('.js-video-player-wrapper'));

// Define options and parameters for supported vendors
const vendors = {
  youtube: {
    known_urls: ['youtube', 'youtu.be'],
    params: {
      default: '&enablejsapi=1',
      autoplay: '&enablejsapi=1&mute=1',
      bg: '&enablejsapi=1&mute=1&showinfo=0&controls=0',
    },
    api_src: '//www.youtube.com/player_api',
    api_loaded: false,
  },
  vimeo: {
    known_urls: ['vimeo'],
    params: {
      default: null,
      autoplay: '&muted=1',
      bg: '&muted=1&title=0&byline=0', // disabling player controls requires Vimeo Pro
    },
    api_src: 'https://player.vimeo.com/api/player.js',
    api_loaded: false,
  },
  wistia: {
    known_urls: ['wistia'],
    params: {
      default: null,
      autoplay: '&muted=true',
      bg: '&muted=true&controlsVisibleOnLoad=false&endVideoBehavior=loop&fullscreenButton=false&playbackRateControl=false&qualityControl=false&settingsControl=false&volumeControl=false',
    },
    api_src: '//fast.wistia.com/assets/external/E-v1.js',
    api_loaded: false,
  },
};


// Populate this array later on, using it to store and reference player objects and elements
const vids = [];


// Dispatch Events
// ----------------------------------

function dispatch_play_event(el) {
  el.dispatchEvent(new CustomEvent('playVideo'));
}


// Play video from any vendor
// ----------------------------------

function play_video(ev) {
  const i = Array.prototype.indexOf.call(vidWraps, ev.target);

  const { vendor } = vids[i];
  const { wrapper } = vids[i];
  const { mode } = vids[i];

  if (vendor === 'youtube') {
    vids[i].obj.playVideo();
  }

  else if (vendor === 'vimeo') {
    vids[i].obj.play().catch((error) => {
      console.error('Error playing the video:', error.name);
    });
  }

  else if (vendor === 'wistia') {
    vids[i].obj.play();
  }

  // TODO: if user has interacted, disable scroll-based auto-stop-start.
  //       for now, disable after it first leaves the viewport.
  if (mode !== 'bg') {
    wrapper.removeEventListener('playVideo', play_video);
    wrapper.removeEventListener('scrollfx/on', play_video);

    vids[i].btn.classList.add('t-fade-out--active');
    vids[i].overlay.classList.add('t-fade-out--active');
    vids[i].wrapper.classList.add(vids[i].active_class);
  }
}


// Pause video from any vendor
// ----------------------------------

function pause_video(ev) {
  const i = Array.prototype.indexOf.call(vidWraps, ev.target);
  const { vendor } = vids[i];

  if (vendor === 'youtube') {
    vids[i].obj.pauseVideo();
  }

  else if (vendor === 'vimeo') {
    vids[i].obj.pause().catch((error) => {
      console.error('Error pausing the video:', error.name);
    });
  }

  else if (vendor === 'wistia') {
    vids[i].obj.pause();
  }
}


// Binding
// ----------------------------------

function bind_btn(btn, i) {
  btn.addEventListener('click', () => {
    dispatch_play_event(vids[i].wrapper);
  });
}

function bind_wrapper(wrapper, mode) {
  wrapper.addEventListener('playVideo', play_video);

  if (mode === 'autoplay' || mode === 'bg') {
    SFX.init(wrapper);
    setTimeout(() => {
      wrapper.addEventListener('scrollfx/on', play_video);
      if (mode === 'autoplay') {
        wrapper.addEventListener('scrollfx/off', pause_video);
      }
    }, 1000);
  }
}


// Initialize each vendor player as per API requirements
// ----------------------------------
// The YouTube API provides a callback function hence initializing all at once, other
// vendors are initialized individually
// ----------------------------------

function init_all_youtube_players() {
  vids.forEach((vid) => {
    if (vid.vendor === 'youtube') {
      vid.obj = new window.YT.Player(vid.iframe);
      bind_wrapper(vid.wrapper, vid.mode);
    }
  });
}

function init_single_vimeo_player(iframe, i) {
  const obj = new window.Vimeo.Player(iframe);
  vids[i].obj = obj;
}

function init_single_wistia_player(iframe, i) {
  iframe.id = `wistia-video-${i}`;
  window._wq.push({
    id: iframe.id,
    onReady: (video) => {
      vids[i].obj = video;
    },
  });
}


// Dom Insertion
// ----------------------------------

const playIcon = `
  <span class="c-icon ">
    <svg><use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="/wp-content/themes/brandwatch/build/sprite-map-2020-pitchbox.svg#core-icon-play"></use></svg>
  </span>
`;

function insert_btn(wrapper, i, ui_color) {
  const btn = create_element(`
    <div class="c-btn-round c-btn-round--2 absolute--em-centered t-fade-out color-text-white" style="color: ${ui_color}">
    <div class="c-btn__color-mix absolute--fill ba br-100 ${ui_color ? 'color-bg-white color-border-white' : 'color-bg-ui-primary color-border-ui-primary'}"></div><div class="c-btn__color absolute--fill br-100 shadow-5 ${ui_color ? 'color-bg-white color-border-white' : 'color-bg-ui-primary color-border-ui-primary'}"></div><div class="c-btn-round__icon-wrap df flex-items-center flex-justify-center">
    ${playIcon}</div></div>
  `);

  vids[i].btn = btn;
  wrapper.appendChild(btn);
  bind_btn(btn, i);
}

function insert_overlay(wrapper, i) {
  const overlay = create_element(`<div class="absolute--fill color-bg-grey-10 o-70 z-1 t-fade-out"></div>`);

  vids[i].overlay = overlay;
  wrapper.appendChild(overlay);
}

function insert_api_script(vendor) {
  if (!vendors[vendor].api_loaded) {
    const scriptTag = document.createElement('script');
    scriptTag.src = vendors[vendor].api_src;
    const [firstScriptTag] = document.getElementsByTagName('script');
    firstScriptTag.parentNode.insertBefore(scriptTag, firstScriptTag);

    if (vendor === 'youtube') {
      window.onYouTubePlayerAPIReady = init_all_youtube_players;
    }
    vendors[vendor].api_loaded = true;
  }
}


// Item Initialization
// ----------------------------------

function init_item(wrapper, i) {
  const [iframe] = wrapper.getElementsByTagName('iframe');
  if (!iframe) {
    console.log('iframe not found for wrapper: ' , wrapper);
    return false;
  };
  Object.keys(vendors).forEach((vendor) => {
    const vendor_supported = vendors[vendor].known_urls.some(el => iframe.src.includes(el));

    if (vendor_supported) {
      let mode = wrapper.getAttribute('data-video-mode') || 'default';
      if (mode === 'bg') {
        mode = 'background';
      }

      iframe.src += vendors[vendor].params[mode];

      const ui_color = wrapper.getAttribute('data-ui-color');
      const active_class = wrapper.getAttribute('data-video-wrapper-active-class');

      if (mode === 'background') {
        iframe.style.pointerEvents = 'none';
      }

      vids[i] = {
        vendor,
        wrapper,
        iframe,
        obj: {},
        mode,
        active_class,
        ui_color,
      };

      insert_api_script(vendor);
    
      if (mode !== 'background' && mode !== 'autoplay') {
        insert_overlay(wrapper, i);
        insert_btn(wrapper, i, ui_color);
      }

      // YouTube initialization is handled by a YT callback function on the window
      // object, mapped to init_all_youtube_players().
      // setTiemout to 1 second to avoid API scripts not being loaded = not nice

      if (vendor === 'vimeo' && vendors[vendor].api_loaded === true) {
        // Use setInterval to wait until Vimeo API is available
        const interval = setInterval(() => {
          if (window.Vimeo) {
            init_single_vimeo_player(iframe, i);
            bind_wrapper(wrapper, mode);

            clearInterval(interval);
          }
        }, 1000);
      }

      if (vendor === 'wistia' && vendors[vendor].api_loaded === true) {
        iframe.classList.add('wistia_embed'); // Must have this class to work

        // Use setInterval to wait until Wistia API is available
        const interval = setInterval(() => {
          if (window._wq) {
            init_single_wistia_player(iframe, i);
            bind_wrapper(wrapper, mode);

            clearInterval(interval);
          }
        }, 1000);
      }
    }
  });
}


// Initialize if any videos present
// ----------------------------------

if (vidWraps.length > 0) {
  setTimeout(() => {
    vidWraps.forEach(init_item);
  }, 1000);
}

